import { FormatterService } from "../../NumberFormatter";

import { ErrorKind, ForecastTarget } from "./services";

// Series colours read from the theme's `chart.*` roles so the ML charts match
// the rest of the app in both colour schemes; every chart here also carries a
// legend and direct labels rather than relying on colour alone.
export const SERIES_COLORS = {
  ml: "var(--mui-palette-chart-forecast)",
  baseline: "var(--mui-palette-chart-model)",
  actual: "var(--mui-palette-chart-positive)",
} as const;

/**
 * Everything below maps to translation keys, not to text: the labels live in the Excel the
 * translation files are generated from. Consumers resolve them with intl.formatMessage.
 */

export const TARGET_LABEL_KEYS: Record<ForecastTarget, string> = {
  nb_uh: "ML_FORECAST_TARGET_NB_UH",
  adr: "ML_FORECAST_TARGET_ADR",
};

/**
 * Unit suffix per target — the room-night model and the price model share no scale. The room
 * night is a word and gets translated; the euro sign is a symbol and does not.
 */
export const NIGHTS_UNIT_KEY = "ML_FORECAST_UNIT_NIGHTS";

/** Names the measure the breakdown charts are scoring, so their title follows the tab. */
export const TARGET_ERROR_LABEL_KEYS: Record<ForecastTarget, string> = {
  nb_uh: "ML_FORECAST_ERROR_ON_NB_UH",
  adr: "ML_FORECAST_ERROR_ON_ADR",
};

export const ERROR_KINDS: ErrorKind[] = ["mae", "bias"];

export const ERROR_KIND_LABEL_KEYS: Record<ErrorKind, string> = {
  mae: "ML_FORECAST_ERROR_KIND_MAE",
  bias: "ML_FORECAST_ERROR_KIND_BIAS",
};

/** Fixed decimals in the reader's locale — toFixed always writes a dot. */
export const decimal = (
  formatter: FormatterService,
  value: number,
  digits = 0
) =>
  formatter.format(value, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

/** The tenant's own currency, which a hardcoded euro sign would get wrong. */
export const money = (formatter: FormatterService, value: number, digits = 0) =>
  formatter.format(value, {
    style: "currency",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

/** The tenant's currency symbol on its own, for labels that name a unit rather than a value. */
export const currencySymbol = (formatter: FormatterService) =>
  formatter
    .formatToParts(0, { style: "currency" })
    .find((part) => part.type === "currency")?.value ?? "";

export const percent = (formatter: FormatterService, value: number) =>
  formatter.format(value, {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

/**
 * Graduations spread evenly along a lead time axis, then snapped to tiers that really exist.
 *
 * The tiers come from the client's own booking curve, so their number and their spacing vary
 * from one client to the next — and they crowd near the stay date, where the pick-up
 * accelerates. Spacing by rank rather than by value would pile every graduation into that
 * corner of the chart.
 */
const AXIS_TICKS = 8;

export const axisTicks = (values: number[]) => {
  if (values.length <= AXIS_TICKS) return values;
  const min = Math.min(...values);
  const wanted = Array.from(
    { length: AXIS_TICKS },
    (_, index) => (min * (AXIS_TICKS - 1 - index)) / (AXIS_TICKS - 1)
  );
  return [
    ...new Set(
      wanted.map((target) =>
        values.reduce((closest, value) =>
          Math.abs(value - target) < Math.abs(closest - target)
            ? value
            : closest
        )
      )
    ),
  ];
};

export const FEATURE_LABEL_KEYS: Record<string, string> = {
  hebergement_code: "ML_FORECAST_FEATURE_HEBERGEMENT",
  uh_type_nandc_code: "ML_FORECAST_FEATURE_UH_TYPE",
  modele_type_code: "ML_FORECAST_FEATURE_MODELE_TYPE",
  day_of_week: "ML_FORECAST_FEATURE_DAY_OF_WEEK",
  month_of_year: "ML_FORECAST_FEATURE_MONTH",
  lead_time: "ML_FORECAST_FEATURE_LEAD_TIME",
  capacity: "ML_FORECAST_FEATURE_CAPACITY",
  rob_nb_uh: "ML_FORECAST_FEATURE_ROB_NB_UH",
  rob_revenue: "ML_FORECAST_FEATURE_ROB_REVENUE",
  occupancy_rate: "ML_FORECAST_FEATURE_OCCUPANCY_RATE",
  adr_on_books: "ML_FORECAST_FEATURE_ADR_ON_BOOKS",
  remaining_capacity: "ML_FORECAST_FEATURE_REMAINING_CAPACITY",
};

/** Stages reported by the training task, in the order they occur. */
export const TRAINING_STEP_LABEL_KEYS: Record<string, string> = {
  queued: "ML_FORECAST_STEP_QUEUED",
  features: "ML_FORECAST_STEP_FEATURES",
  loading: "ML_FORECAST_STEP_LOADING",
  training_nb_uh: "ML_FORECAST_STEP_TRAINING_NB_UH",
  training_adr: "ML_FORECAST_STEP_TRAINING_ADR",
  backtest: "ML_FORECAST_STEP_BACKTEST",
  predicting: "ML_FORECAST_STEP_PREDICTING",
};

export const METRIC_KEY_IDS: Record<
  string,
  { label: string; simple: string; detail: string }
> = {
  mae: {
    label: "ML_FORECAST_METRIC_MAE_LABEL",
    simple: "ML_FORECAST_METRIC_MAE_SIMPLE",
    detail: "ML_FORECAST_METRIC_MAE_DETAIL",
  },
  wape: {
    label: "ML_FORECAST_METRIC_WAPE_LABEL",
    simple: "ML_FORECAST_METRIC_WAPE_SIMPLE",
    detail: "ML_FORECAST_METRIC_WAPE_DETAIL",
  },
  r2: {
    label: "ML_FORECAST_METRIC_R2_LABEL",
    simple: "ML_FORECAST_METRIC_R2_SIMPLE",
    detail: "ML_FORECAST_METRIC_R2_DETAIL",
  },
};

export const METRIC_KEYS = Object.keys(METRIC_KEY_IDS);
